import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import Navigation from '../../shared/Navigation'; 

// Helper for INR formatting
const formatINR = (value) => value.toLocaleString('en-IN', { maximumFractionDigits: 0 });

const PFCalculator = () => {
  const [yearlyInvestment, setYearlyInvestment] = useState(150000);
  const [interestRate, setInterestRate] = useState(7.1);
  const [result, setResult] = useState(null);
  const [chartData, setChartData] = useState(null);

  const calculatePPF = () => {
    const years = 15; // Fixed tenure for PPF
    const yearlyData = [];
    let totalInvestment = 0;
    let balance = 0;

    for (let i = 1; i <= years; i++) {
      totalInvestment += yearlyInvestment;
      balance = (balance + yearlyInvestment) * (1 + interestRate / 100);
      yearlyData.push({
        year: i,
        balance: Math.round(balance),
        investment: totalInvestment
      });
    }

    setResult({
      maturityAmount: Math.round(balance),
      totalInvestment,
      interestEarned: Math.round(balance - totalInvestment)
    });

    setChartData({
      labels: yearlyData.map(d => `Year ${d.year}`),
      datasets: [
        {
          label: 'Balance',
          data: yearlyData.map(d => d.balance),
          borderColor: '#6366f1',
          backgroundColor: 'rgba(99, 102, 241, 0.1)',
          fill: true
        },
        {
          label: 'Investment',
          data: yearlyData.map(d => d.investment),
          borderColor: '#a855f7',
          backgroundColor: 'rgba(168, 85, 247, 0.1)',
          fill: true
        }
      ]
    });
  };

  useEffect(() => {
    calculatePPF();
  }, [yearlyInvestment, interestRate]);

  return (
    <>
      <Helmet>
        <title>PF Calculator - Provident Fund Returns | BuildMyWealth</title>
        <meta name="description" content="Estimate your Provident Fund maturity, interest, and yearly growth with BuildMyWealth's PF Calculator. Plan your retirement corpus efficiently." />
        <meta name="keywords" content="PF Calculator, Provident Fund, PF Returns, PF Maturity, Retirement Planning, BuildMyWealth" />
        <link rel="canonical" href="https://www.buildmywealth.net/calculators/pf" />
      </Helmet>
      <Navigation />
      <div className="w-full max-w-3xl mx-auto py-6 pt-28 px-2 sm:px-4">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
          PF Calculator
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <motion.div className="space-y-4" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Yearly Investment (₹)</label>
              <input
                type="range"
                min="500"
                max="150000"
                step="500"
                value={yearlyInvestment}
                onChange={(e) => setYearlyInvestment(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-500"
              />
              <input
                type="number"
                value={yearlyInvestment}
                onChange={(e) => setYearlyInvestment(Number(e.target.value))}
                className="mt-1 block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Interest Rate (%)</label>
              <input
                type="number"
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                className="block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                step="0.1"
              />
            </div>
            {result && (
              <div className="grid grid-cols-1 gap-2">
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600">Maturity Amount</p>
                  <p className="text-lg font-bold text-indigo-600">₹{formatINR(result.maturityAmount)}</p>
                </div>
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600">Total Investment</p>
                  <p className="text-lg font-bold text-indigo-600">₹{formatINR(result.totalInvestment)}</p>
                </div>
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600">Interest Earned</p>
                  <p className="text-lg font-bold text-indigo-600">₹{formatINR(result.interestEarned)}</p>
                </div>
              </div>
            )}
          </motion.div>
          {chartData && (
            <motion.div className="h-full" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
              <Line
                data={chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'top', labels: { font: { size: 12 } } },
                    title: { display: true, text: 'PF Growth Projection', font: { size: 16 } }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: (value) => `₹${formatINR(value)}`,
                        font: { size: 12 }
                      }
                    },
                    x: {
                      ticks: { font: { size: 12 } }
                    }
                  }
                }}
                height={260}
              />
            </motion.div>
          )}
        </div>
      </div> 
    </>
  );
};

export default PFCalculator;